<template>
  <div v-click-outside="close" class="dash-switcher">
    <button class="ov-switcher" :class="{ open }" @click="toggle">
      <i class="material-icons">dashboard</i>
      <span class="ov-switcher-label">{{ currentName }}</span>
      <i class="material-icons">{{ open ? 'expand_less' : 'expand_more' }}</i>
    </button>

    <transition name="ov-pop">
      <div v-if="open" class="dash-switcher-pop">
        <button class="ov-switcher-item" :class="{ active: $route.name === 'overview' }" @click="openGeneral">
          <i class="material-icons">home</i>
          <span>{{ $t('dash.overview.dashboards.general') }}</span>
        </button>

        <template v-if="mine.length">
          <p class="dash-switcher-group">{{ $t('dash.overview.dashboards.mine') }}</p>
          <button v-for="d in mine" :key="d.id" class="ov-switcher-item" :class="{ active: d.id === currentId }" @click="openDashboard(d)">
            <i class="material-icons">{{ d.visibility === 'shared' ? 'people' : 'lock_outline' }}</i>
            <span>{{ d.name }}</span>
          </button>
        </template>

        <template v-if="shared.length">
          <p class="dash-switcher-group">{{ $t('dash.overview.dashboards.shared_with_me') }}</p>
          <button v-for="d in shared" :key="d.id" class="ov-switcher-item" :class="{ active: d.id === currentId }" @click="openDashboard(d)">
            <i class="material-icons">people</i>
            <span>{{ d.name }}</span>
            <small v-if="d.owner_name">{{ d.owner_name }}</small>
          </button>
        </template>

        <p v-if="loading && !dashboards" class="dash-switcher-empty">{{ $t('dash.overview.updating') }}</p>
        <p v-else-if="error" class="dash-switcher-empty">{{ $t('dash.overview.error') }}</p>

        <button class="ov-switcher-item ov-switcher-item--new" @click="createNew">
          <i class="material-icons">add</i>
          <span>{{ $t('dash.overview.dashboards.new') }}</span>
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import ClickOutside from 'vue-click-outside'
import DashboardsApi from './dashboardsApi'
import { setLastDashboard } from './lastDashboard'

/**
 * Accès aux tableaux de bord : général (overview), les miens, ceux partagés avec moi, création
 */
export default {
  name: 'DashboardSwitcher',

  directives: { ClickOutside },

  mixins: [DashboardsApi],

  props: {
    // null = tableau de bord général
    currentId: { type: String, default: null },
    currentLabel: { type: String, default: '' }
  },

  data() {
    return {
      open: false,
      dashboards: null,
      loading: false,
      error: false
    }
  },

  computed: {
    mine() {
      return (this.dashboards || []).filter(d => d.mine)
    },

    shared() {
      return (this.dashboards || []).filter(d => !d.mine)
    },

    currentName() {
      if (this.currentLabel) return this.currentLabel
      if (!this.currentId) return this.$t('dash.overview.dashboards.general')
      const current = (this.dashboards || []).find(d => d.id === this.currentId)
      return current ? current.name : this.$t('dash.overview.dashboards.general')
    }
  },

  created() {
    this.dashboards = this.cachedDashboards()
    this.refresh()
  },

  methods: {
    async refresh() {
      this.loading = true
      this.error = false
      try {
        this.dashboards = await this.fetchDashboards()
        this.$emit('loaded', this.dashboards)
      } catch (e) {
        this.error = !this.dashboards
      } finally {
        this.loading = false
      }
    },

    toggle() {
      this.open = !this.open
      if (this.open) this.refresh()
    },

    close() {
      this.open = false
    },

    openGeneral() {
      this.close()
      // Choix explicite du général : le menu Overview y ramène désormais
      setLastDashboard(null)
      if (this.$route.name === 'overview') return
      localStorage.setItem('section', 'overview')
      this.$router.push({ name: 'overview' })
    },

    openDashboard(d) {
      this.close()
      if (d.id === this.currentId) return
      this.$router.push({ name: 'customDashboard', params: { id: d.slug || d.id } })
    },

    // Brouillon ouvert directement en édition, enregistré seulement à la validation
    createNew() {
      this.close()
      this.$router.push({ name: 'customDashboard', params: { id: 'new', defaultName: this.defaultDashboardName(this.mine) } })
    }
  }
}
</script>

<style scoped>
.dash-switcher {
  position: relative;
  display: inline-block;
}
.section-overview.ov .dash-switcher button.ov-switcher {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px !important;
  max-width: 320px;
  padding: 0 8px 0 10px;
  border-radius: 20px;
  border: 1.5px solid #e0d0ea;
  background: #fff;
  color: #6b1f4d;
  font-size: 13px !important;
  font-weight: 600 !important;
  transition: background 0.15s;
}
.section-overview.ov .dash-switcher button.ov-switcher:hover,
.section-overview.ov .dash-switcher button.ov-switcher.open {
  background: #f1edf5;
}
.ov-switcher .material-icons {
  font-size: 18px;
}
.ov-switcher-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dash-switcher-pop {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 30;
  display: flex;
  flex-direction: column;
  min-width: 260px;
  max-width: 360px;
  max-height: 420px;
  overflow-y: auto;
  padding: 6px;
  background: #fff;
  border: 1px solid #f1edf5;
  border-top: 3px solid #6b1f4d;
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(107, 31, 77, 0.16);
}
.dash-switcher-group {
  margin: 8px 8px 4px;
  font-size: 10.5px;
  font-weight: 600;
  color: #9ba3b7;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.section-overview.ov .dash-switcher button.ov-switcher-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: auto !important;
  padding: 7px 8px;
  border-radius: 6px;
  color: #121826;
  font-size: 13px !important;
  text-align: left;
}
.section-overview.ov .dash-switcher button.ov-switcher-item:hover {
  background: #f6f1fa;
}
.section-overview.ov .dash-switcher button.ov-switcher-item.active {
  background: #f1edf5;
  color: #6b1f4d;
  font-weight: 600 !important;
}
.ov-switcher-item .material-icons {
  font-size: 17px;
  color: #ab6e93;
}
.ov-switcher-item span {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ov-switcher-item small {
  font-size: 11px;
  color: #9ba3b7;
  white-space: nowrap;
}
.section-overview.ov .dash-switcher button.ov-switcher-item--new {
  margin-top: 4px;
  border-top: 1px solid #f1edf5;
  border-radius: 0 0 6px 6px;
  color: #6b1f4d;
  font-weight: 600 !important;
}
.dash-switcher-empty {
  padding: 6px 8px;
  font-size: 12px;
  color: #9ba3b7;
}
</style>
